import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import MobileToolbar from 'components/Layout/MobileShell/MobileToolbar';
import MobileFilterSheet from 'components/Layout/MobileShell/MobileFilterSheet';
import MobileListState from 'components/Layout/MobileShell/MobileListState';
import { useGetUsinasEolicasQuery } from 'services/api/windx';
import { OrdenacaoUsinas, UsinaEolicaDenormalizada } from 'helper/windx';
import filtrarUsinasEolicas from 'helper/filtrarUsinasEolicas';
import ordenarUsinasEolicas from 'helper/ordenarUsinasEolicas';
import useFiltrosUsinas from 'hooks/Windx/useFiltrosUsinas';
import useSelecaoUsinas from 'hooks/Windx/useSelecaoUsinas';
import {
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import FiltroCategoria from '../FiltroCategoria';
import LinhaSelecao from '../LinhaSelecao';
import Rodape from '../Rodape';

const ORDENACAO_FIXA: OrdenacaoUsinas = { coluna: 'codigo', direcao: 'asc' };

type Categoria = 'uf' | 'sub' | 'conj' | '';

function extrairUnicos(
  itens: UsinaEolicaDenormalizada[],
  campo: (u: UsinaEolicaDenormalizada) => string | null,
): string[] {
  const set = new Set<string>();
  itens.forEach((u) => {
    const v = campo(u);
    if (v !== null && v !== undefined && v !== '') set.add(v);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

function formatarMw(valor: number): string {
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MW`;
}

/**
 * Seletor de usinas eolicas (subdivisao=4) no mobile (WINDX-021).
 *
 * Reusa o fetch unico, `filtrarUsinasEolicas`, `ordenarUsinasEolicas`,
 * `useFiltrosUsinas` e `useSelecaoUsinas` — inclusive o `Set` em `useRef`, que
 * e o que mantem o toggle abaixo de 16ms nas ~1052 usinas. So a apresentacao
 * muda.
 *
 * O filtro de faixa de potencia do desktop fica de fora (D8 nao previu slider
 * no sheet); busca + UF + subsistema + conjunto cobrem o uso mobile.
 */
export default function Usinas({
  open, dismiss, permiteSelecaoVazia,
}: { open: boolean; dismiss: () => void; permiteSelecaoVazia: boolean }) {
  const dispatch = useDispatch();
  const idsSelecionadosRedux = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);

  const usinasQuery = useGetUsinasEolicasQuery(undefined, { skip: !open });
  const usinas = useMemo<UsinaEolicaDenormalizada[]>(
    () => usinasQuery.data?.data ?? [],
    [usinasQuery.data],
  );

  const filtrosHook = useFiltrosUsinas();
  const selecao = useSelecaoUsinas({ inicial: idsSelecionadosRedux });
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [categoriaAberta, setCategoriaAberta] = useState<Categoria>('');

  useEffect(() => {
    if (open) {
      selecao.resetar(idsSelecionadosRedux);
      filtrosHook.limparFiltros();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ufsDisponiveis = useMemo(() => extrairUnicos(usinas, (u) => u.uf_sigla), [usinas]);
  const subsDisponiveis = useMemo(
    () => extrairUnicos(usinas, (u) => u.subsistema_sm_sigla),
    [usinas],
  );
  // WINDX-024: o filtro de conjunto usa `label_conjunto` (desambiguado).
  const conjuntosDisponiveis = useMemo(
    () => extrairUnicos(usinas, (u) => u.label_conjunto),
    [usinas],
  );

  const filtradas = useMemo(
    () => ordenarUsinasEolicas(
      filtrarUsinasEolicas(usinas, filtrosHook.filtrosEfetivos),
      ORDENACAO_FIXA,
    ),
    [usinas, filtrosHook.filtrosEfetivos],
  );

  const usinasPorId = useMemo(() => {
    const m = new Map<number, UsinaEolicaDenormalizada>();
    usinas.forEach((u) => m.set(u.id, u));
    return m;
  }, [usinas]);

  const { filtros } = filtrosHook;
  const chips = [
    ...filtros.ufs,
    ...filtros.subsistemasSm,
    ...filtros.conjuntos,
  ].map((v) => <span key={v}>{v}</span>);

  const toggleCategoria = (cat: Categoria) => () => (
    setCategoriaAberta((atual) => (atual === cat ? '' : cat))
  );

  const handleAplicar = () => {
    // 2026-05-29: nao dispara realizarConsulta — ver SeletorUsinasEolicas.
    dispatch(setIdsEntidades(selecao.ids));
    dismiss();
  };

  const semSelecaoEhTodas = permiteSelecaoVazia && telaAtiva === 'sm' && selecao.count === 0;
  const resumo = selecao.count === 0
    ? 'Nenhuma usina selecionada'
    : `${selecao.count} selecionada${selecao.count === 1 ? '' : 's'} · ${formatarMw(selecao.totalPotencia(usinasPorId))}`;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Selecionar usinas eólicas"
      className="windx-seletor-sheet windx-seletor-sheet--grande"
    >
      <>
        {usinasQuery.error && (
        <Alert variant="danger" className="m-0">
          Não foi possível carregar as usinas. Tente novamente em instantes.
        </Alert>
        )}

        {!usinasQuery.error && (
        <>
          <MobileToolbar
            searchValue={filtros.busca}
            onSearchChange={(e) => filtrosHook.setBusca(e.target.value)}
            setSearchQuery={filtrosHook.setBusca}
            searchId="windx-busca-usinas"
            searchPlaceholder="Buscar usina"
            filterActive={chips.length > 0}
            filterAriaLabel="Filtrar usinas"
            appliedFilters={chips}
            onFilterClick={() => setFiltrosAbertos(true)}
            onClearAll={filtrosHook.limparFiltros}
            resultsCount={filtradas.length}
            resultsNoun="usina"
            loading={usinasQuery.isLoading}
          />

          {semSelecaoEhTodas && usinas.length > 0 && (
            <p className="windx-seletor-sheet-aviso">
              <strong>Nenhuma marcada inclui todas</strong>
              {' — a consulta vai retornar dados das '}
              {usinas.length.toLocaleString('pt-BR')}
              {' usinas.'}
            </p>
          )}

          <div className="windx-seletor-sheet-lista">
            <MobileListState
              loading={usinasQuery.isLoading}
              isEmpty={filtradas.length === 0}
              emptyMessage="Nenhuma usina encontrada para o filtro."
            >
              {filtradas.map((u) => (
                <LinhaSelecao
                  key={u.id}
                  inputId={`windx-usina-mobile-${u.id}`}
                  label={u.nome}
                  detalhe={`${u.codigo} · ${formatarMw(u.potencia)} · ${u.uf_sigla ?? '—'}`}
                  selecionado={selecao.isSelecionada(u.id)}
                  onToggle={() => selecao.toggle(u.id)}
                />
              ))}
            </MobileListState>
          </div>

          <Rodape
            selecionadas={selecao.count}
            resumo={resumo}
            onLimpar={selecao.limpar}
            onCancelar={dismiss}
            onAplicar={handleAplicar}
          />

          <MobileFilterSheet
            open={filtrosAbertos}
            dismiss={() => setFiltrosAbertos(false)}
            title="Filtrar usinas"
            onClear={filtrosHook.limparFiltros}
            onApply={() => setFiltrosAbertos(false)}
          >
            <FiltroCategoria
              label="UF"
              opcoes={ufsDisponiveis}
              selecionadas={filtros.ufs}
              onChange={filtrosHook.setUfs}
              open={categoriaAberta === 'uf'}
              onToggle={toggleCategoria('uf')}
            />
            <FiltroCategoria
              label="Subsistema"
              opcoes={subsDisponiveis}
              selecionadas={filtros.subsistemasSm}
              onChange={filtrosHook.setSubsistemasSm}
              open={categoriaAberta === 'sub'}
              onToggle={toggleCategoria('sub')}
            />
            <FiltroCategoria
              label="Conjunto"
              opcoes={conjuntosDisponiveis}
              selecionadas={filtros.conjuntos}
              onChange={filtrosHook.setConjuntos}
              open={categoriaAberta === 'conj'}
              onToggle={toggleCategoria('conj')}
            />
          </MobileFilterSheet>
        </>
        )}
      </>
    </BottomSheet>
  );
}
